<template>
  <div :id="`div-${elementID}`" class="vue-tag-form">
    <BFormTextarea
      :id="elementID"
      rows="5"
      max-rows="5"
      v-model="statementLogic"
      no-resize
      :placeholder="placeholder"
      autocomplete
    >
    </BFormTextarea>
  </div>
</template>
<script>
import logger from '@/utils/logger';
import Tagify from '@yaireo/tagify';
import { BFormTextarea } from 'bootstrap-vue-next';
import _ from 'lodash';

export default {
  name: 'tag-form',
  components: {
    BFormTextarea
  },
  props: {
    elementID: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    whiteList: {
      type: Array,
      default: () => []
    },
    formData: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:formData'],
  data () {
    return {
      /**
       * Tagify settings passed to the Tagify constructor.
       * The `whitelist` and `transformTag` options are added in setupTagForms()
       * rather than here, because they need access to `this.whiteList` at
       * the time Tagify is created.
       * @type {Tagify.TagifySettings}
       */
      tagOptions: {
        mode: 'mix',
        pattern: /@/,
        // duplicates must be true for formula editing — the same variable can
        // legitimately appear more than once (e.g. X > 0 && X < 100).
        // Setting this false caused already-used items to be filtered out of
        // the dropdown suggestions even when the user explicitly typed @<name>.
        duplicates: true,
        editTags: false,
        dropdown: {
          classname: 'tagform-dropdown',
          position: 'text',
          enabled: 1,
          searchKeys: ['title'],
          mapValueTo: 'title',
          highlightFirst: true,
          // Must be set explicitly. Tagify v4 only auto-syncs this from
          // `duplicates` when it is undefined, but its own defaults set it
          // to false first — so the sync never fires. Without this, the
          // dropdown hides already-used items regardless of duplicates:true.
          includeSelectedTags: true
        },
        enforceWhitelist: true
      },
      tagTextarea: '',
      tagClassName: {
        spanClass: 'tagify__input',
        btnClass: 'tagify__tag__removeBtn'
      },
      statementLogic: ''
    };
  },
  watch: {
    /**
     * When the whitelist loads asynchronously (e.g. from Vuex after an API
     * call), re-initialize Tagify so the display reflects the loaded data.
     * This handles the race condition where created() runs before the store
     * has populated the whitelist.
     *
     * Partial-translation guard: getLogicStatement skips items not yet in the
     * whitelist and leaves their [[Q_xxx]] tokens untranslated. To avoid
     * Tagify receiving a mixed ID/title string, we only set statementLogic and
     * re-initialize when ALL formula tokens can be resolved — i.e. the current
     * statementLogic is still empty (no partial translation was applied).
     */
    whiteList (newList) {
      if (!this._tagify) return;
      // Always sync the whitelist to the live Tagify instance
      this._tagify.settings.whitelist = newList;
      // If the component mounted with an empty whitelist and couldn't render
      // the formula (statementLogic is blank despite formData having a value),
      // re-initialize now that the whitelist is ready.
      // Trigger re-init if statementLogic was never set (whitelist was empty on
      // mount) OR if it still contains unresolved ID tokens from a partial
      // translation (getLogicStatement skips items not yet in the whitelist,
      // leaving tokens like [[Q_5708]], [[PV_123]], [[CV_456]] in place).
      // All IDs follow the pattern PREFIX_value — titles are human-readable and
      // never match this pattern, so the regex reliably distinguishes the two.
      const hasUnresolvedTokens = /\[\[[A-Z]+_[^\]]+\]\]/.test(this.statementLogic);
      if (newList.length > 0 && this.formData && this._tagify.value.length === 0 && (!this.statementLogic || hasUnresolvedTokens)) {
        this.statementLogic = this.getLogicStatement(this.formData);
        this.$nextTick(() => {
          const inputElement = document.getElementById(this.elementID);
          if (inputElement) {
            this.setupTagForms(inputElement);
          }
        });
      }
    }
  },
  created () {
    /**
     * _tagify holds the Tagify instance as a plain (non-reactive) instance
     * property. Keeping it outside of data() prevents Vue from wrapping the
     * Tagify class instance in a Proxy, which would cause Vue to intercept
     * Tagify's internal mutations (e.g. this.value.push(), this.settings.*,
     * this.state.*) and potentially trigger unwanted re-renders or corrupt
     * internal state.
     * @type {Tagify<any> | null}
     */
    this._tagify = null;

    if (this.formData) {
      this.statementLogic = this.getLogicStatement(this.formData);
    }
  },
  mounted () {
    if (this.elementID && this.whiteList) {
      const inputElement = document.getElementById(this.elementID);
      this.setupTagForms(inputElement);

      const divParent = document.getElementById('div-' + this.elementID);
      if (divParent) {
        // spanElement may be absent if setupTagForms failed silently or Tagify
        // hasn't injected its DOM yet — guard to prevent a TypeError.
        const spanElement = divParent.getElementsByClassName(this.tagClassName.spanClass)[0];
        if (!spanElement) return;
        // Note: setting .disabled on a <span> has no browser effect; the
        // keydown listener below is what actually enforces readOnly.
        spanElement.addEventListener('keydown', (e) => {
          if (this.readOnly) {
            e.preventDefault();
          }
        });
        const removeTags = divParent.getElementsByClassName(this.tagClassName.btnClass);
        if (removeTags.length > 0 && this.readOnly) {
          for (let i = 0; i < removeTags.length; i++) {
            removeTags[i].hidden = true;
          }
        }
      }
    }
  },
  beforeUnmount () {
    this.teardownTagify();
  },
  methods: {
    teardownTagify () {
      try {
        if (this._tagify) {
          const tagifyScope = this._tagify.DOM && this._tagify.DOM.scope;
          if (tagifyScope && !tagifyScope.parentNode) {
            this._tagify = null;
            return;
          }
          this._tagify.destroy();
          this._tagify = null;
        }
      } catch (e) {
        logger.error(e);
      }
    },
    setupTagForms (inputElement) {
      if (!inputElement) return;
      try {
        this.teardownTagify();
        const staleTagify = inputElement.parentElement
          ? inputElement.parentElement.querySelector('.tagify')
          : null;
        if (staleTagify) {
          staleTagify.remove();
        }
        if (inputElement.__tagify) {
          inputElement.__tagify = null;
        }
        /**
         * transformTag enriches tag data with the full whitelist item when
         * Tagify creates tags internally (e.g. parseMixTags during init). By
         * default Tagify's parseMixTags produces tag objects with only
         * {title, value} \u2014 no `id`. The id is critical because changeTagFormat
         * reads it to build the formula (e.g. "[[Q_123]] > 2"). Without this
         * hook, the id is lost and the emitted formData would be corrupt.
         */
        const options = {
          ...this.tagOptions,
          whitelist: this.whiteList,
          transformTag: (tagData) => {
            if (!tagData.id && tagData.value) {
              const item = _.find(this.whiteList, { value: tagData.value });
              if (item) {
                tagData.id = item.id;
                tagData.title = item.title;
              }
            }
          }
        };
        this._tagify = new Tagify(inputElement, options);
        this._tagify.on('add', () => {
          this.getInputValue(this._tagify);
        });
        this._tagify.on('remove', () => {
          this.getInputValue(this._tagify);
        });
        this._tagify.on('input', () => {
          this.getInputValue(this._tagify);
        });
      } catch (e) {
        logger.error(e);
      }
    },
    /**
     * Converts the ID-based formula stored in the DB (e.g. "[[Q_123]] > 2")
     * to a title-based display formula (e.g. "[[Question Title]] > 2") so
     * Tagify can render labelled tag chips from the plain-text value in the
     * textarea before it takes over the DOM.
     */
    getLogicStatement (logicStatement) {
      let statementResponse = logicStatement;
      const regexp = /\[(.*?)\]\]/g;
      if (logicStatement) {
        const lsItems = logicStatement.match(regexp);
        if (lsItems && lsItems.length > 0) {
          lsItems.forEach(item => {
            const itemContent = item.replace('[[', '').replace(']]', '');
            const itemObj = _.find(this.whiteList, { 'id': itemContent });
            if (!itemObj) {
              // Whitelist item not found (may not be loaded yet \u2014 the whiteList
              // watcher will re-initialize once it populates).
              logger.warn(`[TagForm] whitelist item not found for id "${itemContent}"`);
              return;
            }
            const itemTitle = '[[' + itemObj.title + ']]';
            // replaceAll handles the unlikely-but-possible case where the same
            // ID token appears more than once in the formula.
            statementResponse = statementResponse.replaceAll(item, itemTitle);
          });
        }
        return statementResponse;
      }
      return '';
    },
    getInputValue (inputDOM) {
      const originalInput = inputDOM.DOM.originalInput;
      if (originalInput) {
        this.changeTagFormat(originalInput.value);
      }
      this.$emit('update:formData', this.tagTextarea);
    },
    /**
     * Converts Tagify's internal serialization format
     * (e.g. "[[{"id":"Q_123","value":"Question"}]] > 2")
     * back to the ID-based formula used by the backend
     * (e.g. "[[Q_123]] > 2").
     *
     * @param {string} transitionText - raw value from Tagify's originalInput
     */
    changeTagFormat (transitionText) {
      if (!transitionText) {
        // The input was fully cleared. Reset tagTextarea so the next emit
        // reports the empty state rather than a stale value from a prior edit.
        this.tagTextarea = '';
        return;
      }
      const tags = transitionText.match(/\{(.*?)\}/g);
      if (tags && tags.length > 0) {
        tags.forEach(tag => {
          let tagData;
          try {
            tagData = JSON.parse(tag);
          } catch {
            logger.warn('[TagForm] could not parse tag JSON:', tag);
            return;
          }
          let id = tagData.id;
          if (!id && tagData.value) {
            // Tag was created without an id (Tagify's parseMixTags builds tags
            // from plain text using normalizeTags, which omits the id). Fall
            // back to a whitelist lookup by the tag's display value.
            const item = _.find(this.whiteList, { value: tagData.value });
            id = item ? item.id : null;
          }
          if (id) {
            // replaceAll ensures every occurrence is substituted in the rare
            // case that the same tag JSON block appears more than once.
            transitionText = transitionText.replaceAll(tag, id);
          } else {
            // No id found \u2014 replace with the tag's display value as a fallback
            // so the formula is visibly wrong rather than silently corrupted.
            logger.warn('[TagForm] could not resolve id for tag value:', tagData.value);
            transitionText = transitionText.replaceAll(tag, tagData.value || '');
          }
        });
      }
      // Remove BOM (\uFEFF), Zero Width characters, and bidirectional
      // control characters used in Trojan Source attacks
      transitionText = transitionText.replaceAll(
        /[\uFEFF\u200B-\u200F\u202A-\u202E\u2066-\u2069]/g,
        ''
      );
      // Tagify v4 serializes the contenteditable content with trailing \n
      // characters that Tagify v2 did not produce. Trim to match prior
      // behaviour and prevent spurious newlines in the saved formula.
      this.tagTextarea = transitionText.trim();
    }
  }
};
</script>
<style lang="scss">
.vue-tag-form {
  width: 100%;
  .tagify--mix {
    border-radius: 5px;
  }
  .tagify__input {
    min-height: 110px;
    line-height: 37px;
    padding: 0.375rem 0.75rem;
  }
  .tagify__tag {
    margin-bottom: 5px;
    div:before {
      background-color: rgba(0, 164, 214, 0.2);
    }
  }
}
.tagform-dropdown {
  --tagify-dd-color-primary: rgba(0, 164, 214, 0.2);
  --tagify-dd-item-pad: 0.35em 0.6em;

  &.tagify__dropdown {
    border: 1px solid #85C8EA;
    box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.2);
  }
  .tagify__dropdown__header {
    display: none;
  }
  .tagify__dropdown__item {
    min-width: 50px;
  }
  .tagify__dropdown__item--active {
    color: var(--tagify-dd-text-color);
  }
}
</style>
